import React, { useState, useRef, useEffect } from "react";
import { Doughnut, Bar, Line } from "react-chartjs-2";
import article from "./StopLossContent";
import WaldoAd from '../AdComponent';
import { 
  Chart, 
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
} from "chart.js";
import './PagesCss/StockProfitCalculator.css';

Chart.register(
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
);

const RiskPerTradeFinder = () => {
  const content = article.find((item) => item.id === 8);
  const [activeTab, setActiveTab] = useState("calculator");
  const [isMobile, setIsMobile] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [optimizationMethod, setOptimizationMethod] = useState("kelly");
  const [notifications, setNotifications] = useState([]);
  const [calculationHistory, setCalculationHistory] = useState([]);

  const [inputs, setInputs] = useState({
    // Account Information
    accountSize: "50000",
    totalCapital: "50000",
    monthlyIncome: "5000",
    riskTolerance: "moderate",
    
    // Trading Performance
    winRate: "55",
    avgWinPercent: "15",
    avgLossPercent: "8",
    totalTrades: "100",
    maxDrawdown: "12",
    
    // Trade Setup
    entryPrice: "100.00",
    stopLossPrice: "95.00",
    positionSize: "5000",
    shares: "50",
    
    // Risk Parameters
    maxPortfolioRisk: "15",
    maxConsecutiveLosses: "5",
    correlationFactor: "0.3",
    volatilityAdjustment: "1.0",
    
    // Market Conditions
    marketVolatility: "Normal",
    strategyType: "Swing Trading",
    timeHorizon: "Medium",
    
    // Advanced Settings
    kellyFraction: "0.5",
    riskOfRuin: "1",
    sharpeRatio: "1.5",
    expectancy: "0.05"
  });

  const [riskAnalysis, setRiskAnalysis] = useState(null);
  const [optimizationResults, setOptimizationResults] = useState(null);
  const chartRef = useRef();

  // Check mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-calculate when inputs change
  useEffect(() => {
    if (activeTab === "calculator" || activeTab === "optimization") {
      calculateOptimalRisk();
    }
  }, [inputs, activeTab, optimizationMethod]);

  const addNotification = (message, type = "info") => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(notif => notif.id !== id));
    }, 5000);
  };

  const handleChange = (e) => {
    setInputs({ ...inputs, [e.target.name]: e.target.value });
  };

  const calculateOptimalRisk = () => {
    const {
      accountSize,
      totalCapital,
      monthlyIncome,
      riskTolerance,
      winRate,
      avgWinPercent,
      avgLossPercent,
      totalTrades,
      maxDrawdown,
      entryPrice,
      stopLossPrice,
      positionSize,
      shares,
      maxPortfolioRisk,
      maxConsecutiveLosses,
      correlationFactor,
      volatilityAdjustment,
      marketVolatility,
      strategyType,
      timeHorizon,
      kellyFraction,
      riskOfRuin,
      sharpeRatio,
      expectancy
    } = inputs;

    if (!accountSize || !winRate || !entryPrice || !stopLossPrice) {
      return;
    }

    const account = parseFloat(accountSize);
    const totalCap = parseFloat(totalCapital) || account;
    const monthlyInc = parseFloat(monthlyIncome) || 0;
    const winPct = parseFloat(winRate) / 100;
    const avgWin = parseFloat(avgWinPercent) / 100;
    const avgLoss = parseFloat(avgLossPercent) / 100;
    const totalTradeCount = parseFloat(totalTrades) || 100;
    const maxDD = parseFloat(maxDrawdown) / 100;
    const entry = parseFloat(entryPrice);
    const stopLoss = parseFloat(stopLossPrice);
    const position = parseFloat(positionSize) || parseFloat(shares) * entry;
    const maxPortfolioRiskPct = parseFloat(maxPortfolioRisk) / 100;
    const maxConsLosses = parseInt(maxConsecutiveLosses) || 5;
    const correlation = parseFloat(correlationFactor) || 0.3;
    const volAdjust = parseFloat(volatilityAdjustment) || 1.0;
    const kellyFrac = parseFloat(kellyFraction) || 0.5;
    const ruinRisk = parseFloat(riskOfRuin) / 100;
    const sharpe = parseFloat(sharpeRatio) || 1.5;
    const exp = parseFloat(expectancy) || 0.05;

    // Calculate trade-specific risk
    const priceRiskPerShare = Math.abs(entry - stopLoss);
    const sharesCount = shares || position / entry;
    const tradeRisk = priceRiskPerShare * sharesCount;
    const currentRiskPercent = (tradeRisk / account) * 100;

    // Calculate optimal risk using different methods
    const kellyRisk = calculateKellyCriterion(winPct, avgWin, avgLoss);
    const fixedFractional = calculateFixedFractional(maxDD, maxConsLosses);
    const riskOfRuinOptimal = calculateRiskOfRuinOptimal(winPct, avgWin, avgLoss, ruinRisk);
    const volatilityBased = calculateVolatilityBased(volAdjust, sharpe);
    const conservative = calculateConservative(maxPortfolioRiskPct, correlation, totalCap, monthlyInc);

    // Get the optimal risk based on selected method
    const optimalRiskPercent = getOptimalRiskByMethod(
      optimizationMethod,
      kellyRisk,
      fixedFractional,
      riskOfRuinOptimal,
      volatilityBased,
      conservative,
      kellyFrac
    );

    const optimalRiskDollars = account * (optimalRiskPercent / 100);
    const optimalShares = Math.floor(optimalRiskDollars / priceRiskPerShare);
    const optimalPositionSize = optimalShares * entry;

    // Risk assessment
    const riskAssessment = assessRiskLevel(
      currentRiskPercent,
      optimalRiskPercent,
      winPct,
      sharpe,
      maxDD
    );

    // Performance projections
    const projections = calculatePerformanceProjections(
      account,
      optimalRiskPercent,
      winPct,
      avgWin,
      avgLoss,
      totalTradeCount
    );

    const analysis = {
      current: {
        riskPercent: currentRiskPercent,
        riskDollars: tradeRisk,
        shares: sharesCount,
        positionSize: position,
        riskPerShare: priceRiskPerShare
      },
      optimal: {
        riskPercent: optimalRiskPercent,
        riskDollars: optimalRiskDollars,
        shares: optimalShares,
        positionSize: optimalPositionSize,
        method: optimizationMethod
      },
      methods: {
        kelly: kellyRisk * 100,
        fixedFractional: fixedFractional * 100,
        riskOfRuin: riskOfRuinOptimal * 100,
        volatilityBased: volatilityBased * 100,
        conservative: conservative * 100
      },
      assessment: riskAssessment,
      projections: projections,
      adjustments: {
        requiredChange: optimalRiskPercent - currentRiskPercent,
        newStopLoss: calculateNewStopLoss(entry, priceRiskPerShare, optimalRiskDollars, sharesCount),
        newPositionSize: optimalPositionSize,
        isAdjustmentNeeded: Math.abs(optimalRiskPercent - currentRiskPercent) > 0.1
      },
      metrics: {
        winRate: winPct * 100,
        avgWin: avgWin * 100,
        avgLoss: avgLoss * 100,
        expectancy: exp,
        sharpeRatio: sharpe,
        maxDrawdown: maxDD * 100
      }
    };

    setRiskAnalysis(analysis);
    calculateOptimizationComparison(analysis);
  };

  const calculateKellyCriterion = (winRate, avgWin, avgLoss) => {
    // Kelly Criterion: f = (bp - q) / b
    // where b = avgWin/avgLoss, p = winRate, q = 1-p
    const b = avgWin / Math.abs(avgLoss);
    const p = winRate;
    const q = 1 - p;
    
    if (b <= 0) return 0.02; // Default 2% if invalid
    
    const kelly = (b * p - q) / b;
    return Math.max(0.01, Math.min(0.25, kelly)); // Cap between 1% and 25%
  };

  const calculateFixedFractional = (maxDrawdown, maxConsecutiveLosses) => {
    // Fixed fractional based on maximum acceptable drawdown
    const riskPerTrade = maxDrawdown / maxConsecutiveLosses;
    return Math.max(0.005, Math.min(0.1, riskPerTrade)); // 0.5% to 10%
  };

  const calculateRiskOfRuinOptimal = (winRate, avgWin, avgLoss, targetRuinRisk) => {
    // Simplified risk of ruin calculation
    const lossProbability = 1 - winRate;
    const riskRatio = Math.abs(avgWin / avgLoss);
    
    if (lossProbability === 0) return 0.1; // 10% if no losses
    
    const optimalRisk = (targetRuinRisk * riskRatio) / (lossProbability * 100);
    return Math.max(0.01, Math.min(0.2, optimalRisk)); // 1% to 20%
  };

  const calculateVolatilityBased = (volatilityAdjustment, sharpeRatio) => {
    // Adjust risk based on volatility and strategy quality
    const baseRisk = 0.02; // 2% base
    const volatilityFactor = 1 / volatilityAdjustment;
    const sharpeFactor = Math.min(2, sharpeRatio / 1.0); // Normalize to 1.0 Sharpe
    
    return Math.max(0.005, Math.min(0.15, baseRisk * volatilityFactor * sharpeFactor));
  };

  const calculateConservative = (maxPortfolioRisk, correlation, totalCapital, monthlyIncome) => {
    // Conservative approach considering total financial picture
    const capitalRisk = maxPortfolioRisk * 0.5; // Use half of max portfolio risk
    const incomeBuffer = monthlyIncome > 0 ? (monthlyIncome * 3) / totalCapital : 0; // 3 months income buffer
    const correlationAdjustment = 1 - (correlation * 0.5);
    
    return Math.max(0.005, Math.min(0.1, capitalRisk * correlationAdjustment - incomeBuffer));
  };

  const getOptimalRiskByMethod = (method, kelly, fixed, ruin, volatility, conservative, kellyFraction) => {
    const methods = {
      kelly: kelly * kellyFraction, // Fractional Kelly
      fixed: fixed,
      ruin: ruin,
      volatility: volatility,
      conservative: conservative,
      adaptive: (kelly * 0.3 + fixed * 0.3 + conservative * 0.4) // Weighted average
    };
    
    return methods[method] || methods.adaptive;
  };

  const assessRiskLevel = (currentRisk, optimalRisk, winRate, sharpeRatio, maxDrawdown) => {
    let score = 50;
    
    // Current vs optimal risk
    const riskDeviation = Math.abs(currentRisk - optimalRisk) / optimalRisk;
    if (riskDeviation <= 0.1) score += 30;
    else if (riskDeviation <= 0.25) score += 15;
    else if (riskDeviation > 0.5) score -= 20;
    
    // Win rate consideration
    if (winRate >= 0.6) score += 20;
    else if (winRate <= 0.4) score -= 15;
    
    // Sharpe ratio
    if (sharpeRatio >= 1.5) score += 15;
    else if (sharpeRatio <= 0.5) score -= 10;
    
    // Drawdown tolerance
    if (maxDrawdown <= 0.1) score += 10;
    else if (maxDrawdown > 0.2) score -= 15;
    
    const normalizedScore = Math.max(0, Math.min(100, score));
    
    if (normalizedScore >= 80) return { level: "Excellent", color: "#28A745", description: "Optimal risk level" };
    if (normalizedScore >= 70) return { level: "Good", color: "#2B8C3A", description: "Appropriate risk" };
    if (normalizedScore >= 60) return { level: "Fair", color: "#FFC107", description: "Moderate risk" };
    if (normalizedScore >= 50) return { level: "Caution", color: "#FF6B6B", description: "Review needed" };
    return { level: "Danger", color: "#E53E3E", description: "High risk detected" };
  };

  const calculatePerformanceProjections = (accountSize, riskPercent, winRate, avgWin, avgLoss, totalTrades) => {
    const riskDollars = accountSize * (riskPercent / 100);
    const avgWinDollars = riskDollars * (avgWin / Math.abs(avgLoss));
    const avgLossDollars = riskDollars;
    
    const expectedValuePerTrade = (winRate * avgWinDollars) - ((1 - winRate) * avgLossDollars);
    const monthlyTrades = Math.min(22, totalTrades / 12); // Assume 22 trading days
    const annualTrades = totalTrades;
    
    return {
      expectedValuePerTrade,
      expectedMonthlyReturn: expectedValuePerTrade * monthlyTrades,
      expectedAnnualReturn: expectedValuePerTrade * annualTrades,
      expectedGrowthRate: (expectedValuePerTrade * annualTrades) / accountSize,
      riskAdjustedReturn: expectedValuePerTrade / riskDollars
    };
  };

  const calculateNewStopLoss = (entryPrice, currentRiskPerShare, optimalRiskDollars, currentShares) => {
    const optimalRiskPerShare = optimalRiskDollars / currentShares;
    const newStopDistance = optimalRiskPerShare;
    
    if (entryPrice > parseFloat(inputs.stopLossPrice)) {
      // Long position
      return entryPrice - newStopDistance;
    } else {
      // Short position
      return entryPrice + newStopDistance;
    }
  };

  const calculateOptimizationComparison = (analysis) => {
    const { methods, optimal, current } = analysis;
    
    const comparison = Object.entries(methods).map(([method, riskPercent]) => ({
      method: method.charAt(0).toUpperCase() + method.slice(1),
      riskPercent,
      positionSize: parseFloat(inputs.accountSize) * (riskPercent / 100),
      differenceFromCurrent: riskPercent - current.riskPercent,
      isOptimal: method === optimal.method
    }));
    
    setOptimizationResults(comparison);
  };

  const generateRecommendations = (analysis) => {
    const recommendations = [];
    const { current, optimal, adjustments, assessment } = analysis;

    if (adjustments.isAdjustmentNeeded) {
      if (current.riskPercent > optimal.riskPercent) {
        recommendations.push({
          type: "warning",
          message: "Current risk higher than optimal",
          action: `Reduce position size to $${adjustments.newPositionSize.toFixed(0)} or adjust stop loss`
        });
      } else {
        recommendations.push({
          type: "info",
          message: "Current risk lower than optimal",
          action: `Consider increasing position size to $${adjustments.newPositionSize.toFixed(0)} for better returns`
        });
      }
    }

    if (assessment.level === "Danger" || assessment.level === "Caution") {
      recommendations.push({
        type: "danger",
        message: "Risk level requires attention",
        action: "Review trade parameters and risk management strategy"
      });
    }

    if (parseFloat(inputs.winRate) < 50) {
      recommendations.push({
        type: "warning",
        message: "Win rate below 50%",
        action: "Focus on improving strategy or use tighter risk management"
      });
    }

    if (parseFloat(inputs.maxDrawdown) > 20) {
      recommendations.push({
        type: "danger",
        message: "High maximum drawdown",
        action: "Implement stricter risk controls and position sizing"
      });
    }

    if (recommendations.length === 0) {
      recommendations.push({
        type: "success",
        message: "Risk parameters well optimized",
        action: "Continue with current risk management approach"
      });
    }

    return recommendations;
  };

  const resetCalculator = () => {
    setInputs({
      accountSize: "50000",
      totalCapital: "50000",
      monthlyIncome: "5000",
      riskTolerance: "moderate",
      winRate: "55",
      avgWinPercent: "15",
      avgLossPercent: "8",
      totalTrades: "100",
      maxDrawdown: "12",
      entryPrice: "100.00",
      stopLossPrice: "95.00",
      positionSize: "5000",
      shares: "50",
      maxPortfolioRisk: "15",
      maxConsecutiveLosses: "5",
      correlationFactor: "0.3",
      volatilityAdjustment: "1.0",
      marketVolatility: "Normal",
      strategyType: "Swing Trading",
      timeHorizon: "Medium",
      kellyFraction: "0.5",
      riskOfRuin: "1",
      sharpeRatio: "1.5",
      expectancy: "0.05"
    });
    addNotification("Calculator reset to default values", "success");
  };

  const exportToCSV = () => {
    if (!riskAnalysis) return;
    
    const csvContent = [
      "Metric,Value",
      `Account Size,$${inputs.accountSize}`,
      `Current Risk Per Trade,${riskAnalysis.current.riskPercent.toFixed(2)}%`,
      `Optimal Risk Per Trade,${riskAnalysis.optimal.riskPercent.toFixed(2)}%`,
      `Optimal Method,${riskAnalysis.optimal.method}`,
      `Current Position Size,$${riskAnalysis.current.positionSize.toFixed(2)}`,
      `Optimal Position Size,$${riskAnalysis.optimal.positionSize.toFixed(2)}`,
      `Kelly Criterion Risk,${riskAnalysis.methods.kelly.toFixed(2)}%`,
      `Fixed Fractional Risk,${riskAnalysis.methods.fixedFractional.toFixed(2)}%`,
      `Risk of Ruin Optimal,${riskAnalysis.methods.riskOfRuin.toFixed(2)}%`,
      `Volatility Based Risk,${riskAnalysis.methods.volatilityBased.toFixed(2)}%`,
      `Conservative Risk,${riskAnalysis.methods.conservative.toFixed(2)}%`,
      `Risk Assessment,${riskAnalysis.assessment.level}`,
      `Expected Annual Return,${(riskAnalysis.projections.expectedGrowthRate * 100).toFixed(2)}%`
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'optimal-risk-analysis.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    addNotification("Analysis exported to CSV successfully", "success");
  };

  const printResult = () => {
    window.print();
  };

  const saveToHistory = () => {
    if (!riskAnalysis) return;
    
    const newCalculation = {
      id: Date.now(),
      timestamp: new Date().toLocaleString(),
      inputs: { ...inputs },
      analysis: riskAnalysis
    };
    
    setCalculationHistory(prev => [newCalculation, ...prev.slice(0, 9)]);
    addNotification("Analysis saved to history", "success");
  };

  const quickPreset = (preset) => {
    const presets = {
      conservative: { 
        riskTolerance: "conservative", 
        maxPortfolioRisk: "10", 
        kellyFraction: "0.25",
        optimizationMethod: "conservative"
      },
      moderate: { 
        riskTolerance: "moderate", 
        maxPortfolioRisk: "15", 
        kellyFraction: "0.5",
        optimizationMethod: "adaptive"
      },
      aggressive: { 
        riskTolerance: "aggressive", 
        maxPortfolioRisk: "20", 
        kellyFraction: "0.75",
        optimizationMethod: "kelly"
      }
    };
    
    setInputs(prev => ({ ...prev, ...presets[preset] }));
    setOptimizationMethod(presets[preset].optimizationMethod);
    addNotification(`${preset} risk preset applied`, "info");
  };

  const strategyPreset = (strategy) => {
    const presets = {
      daytrading: { 
        strategyType: "Day Trading", 
        timeHorizon: "Short",
        volatilityAdjustment: "1.2",
        maxConsecutiveLosses: "8"
      },
      swing: { 
        strategyType: "Swing Trading", 
        timeHorizon: "Medium",
        volatilityAdjustment: "1.0",
        maxConsecutiveLosses: "5"
      },
      position: { 
        strategyType: "Position Trading", 
        timeHorizon: "Long",
        volatilityAdjustment: "0.8",
        maxConsecutiveLosses: "3"
      }
    };
    
    setInputs(prev => ({ ...prev, ...presets[strategy] }));
    addNotification(`${strategy} trading strategy applied`, "info");
  };

  // Chart Data
  const riskComparisonData = riskAnalysis ? {
    labels: ["Current Risk", "Optimal Risk", "Kelly Criterion", "Fixed Fractional", "Risk of Ruin", "Volatility Based", "Conservative"],
    datasets: [{
      label: 'Risk Per Trade (%)',
      data: [
        riskAnalysis.current.riskPercent,
        riskAnalysis.optimal.riskPercent,
        riskAnalysis.methods.kelly,
        riskAnalysis.methods.fixedFractional,
        riskAnalysis.methods.riskOfRuin,
        riskAnalysis.methods.volatilityBased,
        riskAnalysis.methods.conservative
      ],
      backgroundColor: [
        '#FF6B6B',
        '#28A745',
        'rgba(25, 105, 44, 0.6)',
        'rgba(40, 167, 69, 0.6)',
        'rgba(255, 193, 7, 0.6)',
        'rgba(255, 107, 107, 0.6)',
        'rgba(229, 62, 62, 0.6)'
      ],
      borderColor: '#19692C',
      borderWidth: 2
    }]
  } : null;

  const performanceProjectionData = riskAnalysis ? {
    labels: ["Expected Trade", "Expected Month", "Expected Year", "Growth Rate"],
    datasets: [{
      label: 'Projected Value ($)',
      data: [
        riskAnalysis.projections.expectedValuePerTrade,
        riskAnalysis.projections.expectedMonthlyReturn,
        riskAnalysis.projections.expectedAnnualReturn,
        riskAnalysis.projections.expectedGrowthRate * parseFloat(inputs.accountSize)
      ],
      backgroundColor: [
        'rgba(25, 105, 44, 0.6)',
        'rgba(40, 167, 69, 0.6)',
        'rgba(255, 193, 7, 0.6)',
        'rgba(255, 107, 107, 0.6)'
      ],
      borderColor: '#19692C',
      borderWidth: 2
    }]
  } : null;

  const methodComparisonData = optimizationResults ? {
    labels: optimizationResults.map(r => r.method),
    datasets: [{
      label: 'Risk Percentage (%)',
      data: optimizationResults.map(r => r.riskPercent),
      backgroundColor: optimizationResults.map(r => r.isOptimal ? '#28A745' : '#19692C'),
      borderColor: '#19692C',
      borderWidth: 2
    }]
  } : null;

  const riskAssessment = riskAnalysis ? riskAnalysis.assessment : null;
  const recommendations = riskAnalysis ? generateRecommendations(riskAnalysis) : [];

  return (
    <div className={'profit-calculator'}>
      {/* Notifications */}
      <div className="notifications-container">
        {notifications.map(notif => (
          <div key={notif.id} className={`notification ${notif.type}`}>
            <i className={`fas fa-${notif.type === 'success' ? 'check' : 'info'}-circle`}></i>
            {notif.message}
          </div>
        ))}
      </div>

      <div className="calculator-header">
        <div className="header-top">
          <h1>Risk Per Trade Finder</h1>
        </div>
        <p>Find your optimal risk per trade using multiple mathematical approaches and risk models</p>
      </div>
<WaldoAd type="horizontal" /><br/>
      <div className="calculator-layout">
        {/* Left Side - Inputs */}
        <div className="left-panel">
          {/* Navigation Tabs */}
          <div className="tabs-container">
            <div className="tabs">
              <button 
                className={`tab ${activeTab === "calculator" ? "active" : ""}`}
                onClick={() => setActiveTab("calculator")}
              >
                <i className="fas fa-calculator"></i>
                {isMobile ? 'Calculator' : 'Risk Calculator'}
              </button>
              <button 
                className={`tab ${activeTab === "optimization" ? "active" : ""}`}
                onClick={() => setActiveTab("optimization")}
              >
                <i className="fas fa-chart-line"></i>
                {isMobile ? 'Optimization' : 'Method Comparison'}
              </button>
              <button 
                className={`tab ${activeTab === "projections" ? "active" : ""}`}
                onClick={() => setActiveTab("projections")}
              >
                <i className="fas fa-project-diagram"></i>
                {isMobile ? 'Projections' : 'Performance'}
              </button>
              <button 
                className={`tab ${activeTab === "history" ? "active" : ""}`}
                onClick={() => setActiveTab("history")}
              >
                <i className="fas fa-history"></i>
                {isMobile ? 'History' : 'History'}
              </button>
            </div>
          </div>

          {/* Risk Profile Presets */}
          <div className="presets-section">
            <h4>Risk Profile:</h4>
            <div className="preset-buttons">
              <button className="preset-btn conservative" onClick={() => quickPreset('conservative')}>
                Conservative
              </button>
              <button className="preset-btn moderate" onClick={() => quickPreset('moderate')}>
                Moderate
              </button>
              <button className="preset-btn aggressive" onClick={() => quickPreset('aggressive')}>
                Aggressive
              </button>
            </div>
          </div>

          {/* Optimization Method Selection */}
          <div className="mode-selection">
            <div className="mode-buttons">
              <button 
                className={`mode-btn ${optimizationMethod === "kelly" ? "active" : ""}`}
                onClick={() => setOptimizationMethod("kelly")}
              >
                <i className="fas fa-calculator"></i>
                Kelly Criterion
              </button>
              <button 
                className={`mode-btn ${optimizationMethod === "fixed" ? "active" : ""}`}
                onClick={() => setOptimizationMethod("fixed")}
              >
                <i className="fas fa-chart-line"></i>
                Fixed Fractional
              </button>
              <button 
                className={`mode-btn ${optimizationMethod === "adaptive" ? "active" : ""}`}
                onClick={() => setOptimizationMethod("adaptive")}
              >
                <i className="fas fa-brain"></i>
                Adaptive
              </button>
            </div>
            <div className="mode-description">
              {optimizationMethod === "kelly" 
                ? "Mathematical optimization based on win rate and payoff ratio"
                : optimizationMethod === "fixed"
                ? "Fixed percentage risk based on drawdown tolerance"
                : "Adaptive approach combining multiple methods for balanced risk"
              }
            </div>
          </div>

          {/* Strategy Presets */}
          <div className="presets-section">
            <h4>Trading Strategy:</h4>
            <div className="preset-buttons">
              <button className="preset-btn conservative" onClick={() => strategyPreset('daytrading')}>
                Day Trading
              </button>
              <button className="preset-btn moderate" onClick={() => strategyPreset('swing')}>
                Swing Trading
              </button>
              <button className="preset-btn aggressive" onClick={() => strategyPreset('position')}>
                Position Trading
              </button>
            </div>
          </div>

          {/* Risk Calculator Tab */}
          {activeTab === "calculator" && (
            <div className="calculator-content">
              <div className="input-section">
                <div className="input-group">
                  <h3>
                    <i className="fas fa-wallet"></i>
                    Account Information
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-dollar-sign"></i> Trading Account Size</label>
                      <input 
                        type="number" 
                        name="accountSize" 
                        value={inputs.accountSize} 
                        onChange={handleChange}
                        placeholder="50000"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-piggy-bank"></i> Total Capital</label>
                      <input 
                        type="number" 
                        name="totalCapital" 
                        value={inputs.totalCapital} 
                        onChange={handleChange}
                        placeholder="50000"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-money-bill-wave"></i> Monthly Income</label>
                      <input 
                        type="number" 
                        name="monthlyIncome" 
                        value={inputs.monthlyIncome} 
                        onChange={handleChange}
                        placeholder="5000"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-user-shield"></i> Risk Tolerance</label>
                      <select name="riskTolerance" value={inputs.riskTolerance} onChange={handleChange}>
                        <option value="conservative">Conservative</option>
                        <option value="moderate">Moderate</option>
                        <option value="aggressive">Aggressive</option>
                      </select>
                    </div>
                  </div>

                  <h3>
                    <i className="fas fa-chart-line"></i>
                    Trading Performance
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-trophy"></i> Win Rate (%)</label>
                      <input 
                        type="number" 
                        name="winRate" 
                        value={inputs.winRate} 
                        onChange={handleChange}
                        placeholder="55"
                        step="0.1"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-arrow-up"></i> Average Win (%)</label>
                      <input 
                        type="number" 
                        name="avgWinPercent" 
                        value={inputs.avgWinPercent} 
                        onChange={handleChange}
                        placeholder="15"
                        step="0.1"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-arrow-down"></i> Average Loss (%)</label>
                      <input 
                        type="number" 
                        name="avgLossPercent" 
                        value={inputs.avgLossPercent} 
                        onChange={handleChange}
                        placeholder="8"
                        step="0.1"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-chart-bar"></i> Total Trades</label>
                      <input 
                        type="number" 
                        name="totalTrades" 
                        value={inputs.totalTrades} 
                        onChange={handleChange}
                        placeholder="100"
                      />
                    </div>
                  </div>

                  <h3>
                    <i className="fas fa-exclamation-triangle"></i>
                    Risk History
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-arrow-down"></i> Max Drawdown (%)</label>
                      <input 
                        type="number" 
                        name="maxDrawdown" 
                        value={inputs.maxDrawdown} 
                        onChange={handleChange}
                        placeholder="12"
                        step="0.1"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-bolt"></i> Max Consecutive Losses</label>
                      <input 
                        type="number" 
                        name="maxConsecutiveLosses" 
                        value={inputs.maxConsecutiveLosses} 
                        onChange={handleChange}
                        placeholder="5"
                      />
                    </div>
                  </div>

                  <h3>
                    <i className="fas fa-trade"></i>
                    Current Trade Setup
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-arrow-right"></i> Entry Price</label>
                      <input 
                        type="number" 
                        name="entryPrice" 
                        value={inputs.entryPrice} 
                        onChange={handleChange}
                        placeholder="100.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-arrow-down"></i> Stop Loss Price</label>
                      <input 
                        type="number" 
                        name="stopLossPrice" 
                        value={inputs.stopLossPrice} 
                        onChange={handleChange}
                        placeholder="95.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-dollar-sign"></i> Position Size</label>
                      <input 
                        type="number" 
                        name="positionSize" 
                        value={inputs.positionSize} 
                        onChange={handleChange}
                        placeholder="5000"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-layer-group"></i> Shares</label>
                      <input 
                        type="number" 
                        name="shares" 
                        value={inputs.shares} 
                        onChange={handleChange}
                        placeholder="50"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="action-buttons">
                <button className="btn reset-btn" onClick={resetCalculator}>
                  <i className="fas fa-redo"></i>
                  Reset
                </button>
                <button className="btn save-btn" onClick={saveToHistory}>
                  <i className="fas fa-save"></i>
                  Save Analysis
                </button>
              </div>
            </div>
          )}
<WaldoAd type="square" />
          {/* Optimization Tab */}
          {activeTab === "optimization" && (
            <div className="projection-content">
              <div className="input-section">
                <h3>
                  <i className="fas fa-cogs"></i>
                  Risk Parameters
                </h3>
                <div className="input-row">
                  <div className="input-field">
                    <label><i className="fa-solid fa-shield-alt"></i> Max Portfolio Risk (%)</label>
                    <input 
                      type="number" 
                      name="maxPortfolioRisk" 
                      value={inputs.maxPortfolioRisk} 
                      onChange={handleChange}
                      placeholder="15"
                      step="0.1"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-link"></i> Correlation Factor</label>
                    <select name="correlationFactor" value={inputs.correlationFactor} onChange={handleChange}>
                      <option value="0.1">Low (0.1)</option>
                      <option value="0.3">Medium (0.3)</option>
                      <option value="0.5">High (0.5)</option>
                      <option value="0.7">Very High (0.7)</option>
                    </select>
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-wave-square"></i> Volatility Adjustment</label>
                    <select name="volatilityAdjustment" value={inputs.volatilityAdjustment} onChange={handleChange}>
                      <option value="0.5">Very Low (0.5x)</option>
                      <option value="0.8">Low (0.8x)</option>
                      <option value="1.0">Normal (1.0x)</option>
                      <option value="1.2">High (1.2x)</option>
                      <option value="1.5">Very High (1.5x)</option>
                    </select>
                  </div>
                </div><br/>

                <h3>
                  <i className="fas fa-sliders-h"></i>
                  Advanced Settings
                </h3>
                <div className="input-row">
                  <div className="input-field">
                    <label><i className="fa-solid fa-calculator"></i> Kelly Fraction</label>
                    <select name="kellyFraction" value={inputs.kellyFraction} onChange={handleChange}>
                      <option value="0.25">Quarter Kelly (0.25)</option>
                      <option value="0.5">Half Kelly (0.5)</option>
                      <option value="0.75">Three Quarters (0.75)</option>
                      <option value="1.0">Full Kelly (1.0)</option>
                    </select>
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-skull-crossbones"></i> Risk of Ruin (%)</label>
                    <input 
                      type="number" 
                      name="riskOfRuin" 
                      value={inputs.riskOfRuin} 
                      onChange={handleChange}
                      placeholder="1"
                      step="0.1"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-star"></i> Sharpe Ratio</label>
                    <input 
                      type="number" 
                      name="sharpeRatio" 
                      value={inputs.sharpeRatio} 
                      onChange={handleChange}
                      placeholder="1.5"
                      step="0.1"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-chart-line"></i> Expectancy</label>
                    <input 
                      type="number" 
                      name="expectancy" 
                      value={inputs.expectancy} 
                      onChange={handleChange}
                      placeholder="0.05"
                      step="0.01"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* History Tab */}
          {activeTab === "history" && (
            <div className="history-content">
              <h3>
                <i className="fas fa-history"></i>
                Risk Analysis History
              </h3>
              {calculationHistory.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-inbox fa-3x"></i>
                  <p>No analyses yet. Start optimizing to see your history here.</p>
                </div>
              ) : (
                <div className="history-list">
                  {calculationHistory.map(calc => (
                    <div key={calc.id} className="history-item">
                      <div className="history-header">
                        <span className="history-date">
                          <i className="fas fa-clock"></i>
                          {calc.timestamp}
                        </span>
                        <span className="history-profit" style={{ color: calc.analysis.assessment.color }}>
                          <i className="fas fa-chart-line"></i>
                          {calc.analysis.optimal.riskPercent.toFixed(1)}% Risk
                        </span> 
                      </div>
                      <div className="history-details">
                        <span><i className="fas fa-calculator"></i> Method: {calc.analysis.optimal.method}</span>
                        <span><i className="fas fa-balance-scale"></i> Assessment: {calc.analysis.assessment.level}</span>
                        <span><i className="fas fa-dollar-sign"></i> Position: ${calc.analysis.optimal.positionSize.toFixed(0)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Side - Results */}
        {(activeTab === "calculator" || activeTab === "optimization" || activeTab === "projections") && (
          <div className="right-panel">
            {activeTab === "calculator" && (
              <div className="results-section">
                <div className="results-header">
                  <h2>
                    <i className="fas fa-calculator"></i>
                    Optimal Risk Analysis
                  </h2>
                  <div className="result-actions">
                    <button className="btn export-btn" onClick={exportToCSV}>
                      <i className="fas fa-file-export"></i>
                      {isMobile ? 'CSV' : 'Export CSV'}
                    </button>
                    <button className="btn print-btn" onClick={printResult}>
                      <i className="fas fa-print"></i>
                      {isMobile ? 'Print' : 'Print'}
                    </button>
                  </div>
                </div>
<WaldoAd type="square" />
                {riskAnalysis ? (
                  <>
                    {/* Risk Summary */}
                    <div className="results-grid">
                      <div className="result-card highlight">
                        <div className="result-value" style={{ color: '#FFFFFF' }}>
                          {riskAnalysis.optimal.riskPercent.toFixed(2)}%
                        </div>
                        <div className="result-label">
                          <i className="fas fa-bullseye"></i>
                          Optimal Risk
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value" style={{ 
                          color: riskAnalysis.current.riskPercent > riskAnalysis.optimal.riskPercent ? '#E53E3E' : '#28A745' 
                        }}>
                          {riskAnalysis.current.riskPercent.toFixed(2)}%
                        </div>
                        <div className="result-label">
                          <i className="fas fa-chart-line"></i>
                          Current Risk
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          ${riskAnalysis.optimal.positionSize.toFixed(0)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-dollar-sign"></i>
                          Optimal Position
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value" style={{ color: riskAssessment.color }}>
                          {riskAssessment.level}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-flag"></i>
                          Risk Assessment
                        </div>
                      </div>
                    </div>

                    {/* Risk Assessment */}
                    <div className="risk-summary">
                      <h4>Risk Assessment: <span style={{ color: riskAssessment.color }}>{riskAssessment.level} - {riskAssessment.description}</span></h4>
                      <div className="risk-meter">
                        <div className="meter-bar">
                          <div 
                            className="meter-fill"
                            style={{ 
                              width: `${Math.min(riskAnalysis.current.riskPercent * 5, 100)}%`,
                              backgroundColor: riskAssessment.color
                            }}
                          ></div>
                        </div>
                        <div className="meter-value" style={{ color: riskAssessment.color }}>
                          Current Risk: {riskAnalysis.current.riskPercent.toFixed(2)}% vs Optimal: {riskAnalysis.optimal.riskPercent.toFixed(2)}%
                        </div>
                      </div>
                    </div>

                    {/* Detailed Analysis */}
                    <div className="detailed-results">
                      <div className="detail-row">
                        <span><i className="fas fa-calculator"></i> Optimization Method:</span>
                        <span>{riskAnalysis.optimal.method.charAt(0).toUpperCase() + riskAnalysis.optimal.method.slice(1)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-dollar-sign"></i> Current Position Size:</span>
                        <span>${riskAnalysis.current.positionSize.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-bullseye"></i> Optimal Position Size:</span>
                        <span>${riskAnalysis.optimal.positionSize.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-balance-scale"></i> Required Adjustment:</span>
                        <span style={{ 
                          color: riskAnalysis.adjustments.requiredChange > 0 ? '#28A745' : '#E53E3E'
                        }}>
                          {riskAnalysis.adjustments.requiredChange > 0 ? '+' : ''}{riskAnalysis.adjustments.requiredChange.toFixed(2)}%
                        </span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-ruler"></i> Risk Per Share:</span>
                        <span>${riskAnalysis.current.riskPerShare.toFixed(2)}</span>
                      </div>
                    </div>

                    {/* Risk Comparison Chart */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-chart-bar"></i>
                        Risk Method Comparison
                      </h3>
                      <Bar 
                        data={riskComparisonData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              display: false
                            }
                          },
                          scales: {
                            y: {
                              beginAtZero: true,
                              title: {
                                display: true,
                                text: 'Risk Percentage (%)'
                              }
                            }
                          }
                        }}
                      />
                    </div>

                    {/* Recommendations */}
                    <div className="risk-summary">
                      <h4>Risk Management Recommendations</h4>
                      <div className="scenario-list">
                        {recommendations.map((rec, index) => (
                          <div key={index} className="scenario-item">
                            <div className="scenario-name">
                              <i className={`fas fa-${rec.type === 'success' ? 'check' : rec.type === 'warning' ? 'exclamation-triangle' : 'exclamation-circle'} fa-xs`}></i>
                              {rec.message}
                            </div>
                            <div className="scenario-outcome">{rec.action}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="empty-results">
                    <i className="fas fa-calculator fa-3x"></i>
                    <p>Enter your trading parameters to find optimal risk</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === "optimization" && optimizationResults && (
              <div className="projection-results">
                <h3>
                  <i className="fas fa-chart-line"></i>
                  Method Comparison
                </h3>

                {/* Method Comparison */}
                <div className="projection-summary">
                  {optimizationResults.map((result, index) => (
                    <div key={index} className="summary-card">
                      <div className="summary-value" style={{ color: result.isOptimal ? '#28A745' : '#19692C' }}>
                        {result.riskPercent.toFixed(2)}%
                      </div>
                      <div className="summary-label">
                        <i className="fas fa-calculator"></i>
                        {result.method}
                        {result.isOptimal && <span style={{ color: '#28A745', fontSize: '0.8em', marginLeft: '5px' }}>✓</span>}
                      </div>
                    </div>
                  ))}
                </div><br/>

                {/* Method Comparison Chart */}
                <div className="chart-container">
                  <h3>
                    <i className="fas fa-chart-bar"></i>
                    Optimization Methods Comparison
                  </h3>
                  <Bar 
                    data={methodComparisonData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: true,
                      plugins: {
                        legend: {
                          display: false
                        }
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Risk Percentage (%)'
                          }
                        }
                      }
                    }}
                  />
                </div>

                {/* Method Descriptions */}
                <div className="risk-summary">
                  <h4>Optimization Methods</h4>
                  <div className="scenario-list">
                    <div className="scenario-item">
                      <div className="scenario-name">Kelly Criterion</div>
                      <div className="scenario-outcome">Mathematical optimization for maximum growth</div>
                    </div>
                    <div className="scenario-item">
                      <div className="scenario-name">Fixed Fractional</div>
                      <div className="scenario-outcome">Fixed percentage based on drawdown tolerance</div>
                    </div>
                    <div className="scenario-item">
                      <div className="scenario-name">Risk of Ruin</div>
                      <div className="scenario-outcome">Minimizes probability of account destruction</div>
                    </div>
                    <div className="scenario-item">
                      <div className="scenario-name">Volatility Based</div>
                      <div className="scenario-outcome">Adjusts risk based on market conditions</div>
                    </div>
                    <div className="scenario-item">
                      <div className="scenario-name">Conservative</div>
                      <div className="scenario-outcome">Prioritizes capital preservation</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "projections" && riskAnalysis && (
              <div className="risk-results-panel">
                <h3>
                  <i className="fas fa-project-diagram"></i>
                  Performance Projections
                </h3>
                
                {/* Performance Metrics */}
                <div className="scenario-comparison">
                  <div className="scenario-item">
                    <div className="scenario-name">Expected Performance</div>
                    <div className="scenario-stats">
                      <div className="breakdown-item">
                        <span>Expected Value Per Trade:</span>
                        <span style={{ color: riskAnalysis.projections.expectedValuePerTrade >= 0 ? '#28A745' : '#E53E3E' }}>
                          ${riskAnalysis.projections.expectedValuePerTrade.toFixed(2)}
                        </span>
                      </div>
                      <div className="breakdown-item">
                        <span>Expected Monthly Return:</span>
                        <span style={{ color: riskAnalysis.projections.expectedMonthlyReturn >= 0 ? '#28A745' : '#E53E3E' }}>
                          ${riskAnalysis.projections.expectedMonthlyReturn.toFixed(2)}
                        </span>
                      </div>
                      <div className="breakdown-item">
                        <span>Expected Annual Return:</span>
                        <span style={{ color: riskAnalysis.projections.expectedAnnualReturn >= 0 ? '#28A745' : '#E53E3E' }}>
                          ${riskAnalysis.projections.expectedAnnualReturn.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="scenario-item">
                    <div className="scenario-name">Growth Projections</div>
                    <div className="scenario-stats">
                      <div className="breakdown-item">
                        <span>Expected Growth Rate:</span>
                        <span style={{ color: riskAnalysis.projections.expectedGrowthRate >= 0 ? '#28A745' : '#E53E3E' }}>
                          {(riskAnalysis.projections.expectedGrowthRate * 100).toFixed(2)}%
                        </span>
                      </div>
                      <div className="breakdown-item">
                        <span>Risk-Adjusted Return:</span>
                        <span style={{ color: riskAnalysis.projections.riskAdjustedReturn >= 0 ? '#28A745' : '#E53E3E' }}>
                          {(riskAnalysis.projections.riskAdjustedReturn * 100).toFixed(2)}%
                        </span>
                      </div>
                      <div className="breakdown-item">
                        <span>Win Rate:</span>
                        <span>{riskAnalysis.metrics.winRate.toFixed(1)}%</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Performance Projection Chart */}
                <div className="chart-container">
                  <h3>
                    <i className="fas fa-chart-bar"></i>
                    Expected Performance
                  </h3>
                  <Bar 
                    data={performanceProjectionData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: true,
                      plugins: {
                        legend: {
                          display: false
                        }
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          ticks: {
                            callback: function(value) {
                              return '$' + value.toLocaleString(undefined, {maximumFractionDigits: 0});
                            }
                          }
                        }
                      }
                    }}
                  />
                </div>

                {/* Risk-Return Analysis */}
                <div className="risk-summary">
                  <h4>Risk-Return Analysis</h4>
                  <div className="risk-metrics">
                    <div className="risk-metric">
                      <span className="metric-label">Optimal Risk Level:</span>
                      <span className="metric-value" style={{ color: riskAssessment.color }}>
                        {riskAnalysis.optimal.riskPercent.toFixed(2)}%
                      </span>
                    </div>
                    <div className="risk-metric">
                      <span className="metric-label">Expected Annual Return:</span>
                      <span className="metric-value" style={{ color: riskAnalysis.projections.expectedGrowthRate >= 0 ? '#28A745' : '#E53E3E' }}>
                        {(riskAnalysis.projections.expectedGrowthRate * 100).toFixed(2)}%
                      </span>
                    </div>
                    <div className="risk-metric">
                      <span className="metric-label">Risk-Return Ratio:</span>
                      <span className="metric-value">
                        {(riskAnalysis.projections.expectedGrowthRate / (riskAnalysis.optimal.riskPercent / 100)).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <br/><WaldoAd type="horizontal" />
      {content && content.content}
    </div>
  );
};

export default RiskPerTradeFinder;